import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import Modal from '../../components/Modal';
import Pagination from '../../components/common/Pagination';
import { fetchRoleGroups, fetchPermissions, updateRoleGroup, deleteRoleGroup } from '../../services/api';
import { useTranslation } from '../../context/LanguageContext';
import ManagementHeader from '../management/ManagementHeader';
import MgField from '../management/MgField';
import PermissionTree, { groupByCategory } from '../management/PermissionTree';
import '../management/management.css';

const DASH = '—';

function RoleManagementPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // 페이지네이션 상태
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  const [selectedRole, setSelectedRole] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 수정 모드 관련 state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    code: '',
    description: '',
    permissionIds: []
  });

  // 전체 권한 목록
  const [allPermissions, setAllPermissions] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [roleGroupsData, permissionsData] = await Promise.all([
          fetchRoleGroups(),
          fetchPermissions()
        ]);


        setRoles(roleGroupsData);
        setAllPermissions(permissionsData);
      } catch (err) {
        console.error('데이터 조회 실패:', err);
        alert(t('pages.user.role.alertLoadFail'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const filteredRoles = useMemo(() => {
    return roles.filter(role =>
      role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.code?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [roles, searchTerm]);

  const paginatedRoles = useMemo(() => {
    const startIndex = page * size;
    return filteredRoles.slice(startIndex, startIndex + size);
  }, [filteredRoles, page, size]);

  const totalElements = filteredRoles.length;
  const totalPages = Math.ceil(totalElements / size);

  const handleSizeChange = (newSize) => {
    setSize(newSize);
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  /* 행을 누르면 조회 모드, 표의 연필 버튼은 수정 모드로 바로 연다 */
  const handleRoleClick = (role, edit = false) => {
    setSelectedRole(role);
    setIsEditMode(edit);
    setEditForm({
      name: role.name,
      code: role.code,
      description: role.description || '',
      permissionIds: role.permissions ? role.permissions.map(p => p.id) : []
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRole(null);
    setIsEditMode(false);
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditForm({
      name: selectedRole.name,
      code: selectedRole.code,
      description: selectedRole.description || '',
      permissionIds: selectedRole.permissions ? selectedRole.permissions.map(p => p.id) : []
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionToggle = (permissionId) => {
    setEditForm(prev => {
      const isSelected = prev.permissionIds.includes(permissionId);
      return {
        ...prev,
        permissionIds: isSelected
          ? prev.permissionIds.filter(id => id !== permissionId)
          : [...prev.permissionIds, permissionId]
      };
    });
  };

  const handleUpdateSubmit = async () => {
    try {
      const updatedRoleGroup = await updateRoleGroup(selectedRole.id, editForm);

      const roleGroupsData = await fetchRoleGroups();
      setRoles(roleGroupsData);

      setSelectedRole(updatedRoleGroup);
      setIsEditMode(false);
      alert(t('pages.user.role.alertUpdateSuccess'));
    } catch (error) {
      console.error('권한 그룹 수정 실패:', error);
      alert(t('pages.user.role.alertUpdateFail'));
    }
  };

  const handleDeleteClick = (role) => {
    setRoleToDelete(role);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteRoleGroup(roleToDelete.id);

      setRoles(roles.filter(r => r.id !== roleToDelete.id));
      setIsDeleteModalOpen(false);
      setRoleToDelete(null);

      alert(t('pages.user.role.alertDeleted', { name: roleToDelete.name }));
    } catch (error) {
      console.error('권한 그룹 삭제 실패:', error);
      alert(t('pages.user.role.alertDeleteFail'));
    }
  };

  const etcLabel = t('pages.user.role.misc.etcCategory');

  return (
    <MainLayout>
      <div className="mg-page">
        <ManagementHeader rootLabel={t('sidebar.user.title')} icon="manage_accounts" crumbs={[{ label: t('sidebar.user.role') }]}>
          <button type="button" className="mg-btn primary" onClick={() => navigate('/role-registration')}>
            <span className="material-symbols-outlined">add</span>
            {t('pages.user.role.addBtn')}
          </button>
        </ManagementHeader>

        <div className="mg-panel fill">
          <div className="mg-toolbar">
            <div className="mg-toolbar-right">
              <div className="mg-search">
                <input
                  type="text"
                  className="mg-input"
                  placeholder={t('pages.user.role.searchPlaceholder')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <span className="material-symbols-outlined">search</span>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="mg-loading">{t('pages.user.role.loading')}</div>
          ) : (
            <>
              <div className="mg-table-wrap">
                <table className="mg-table">
                  <thead>
                    <tr>
                      <th className="left">{t('pages.user.role.col.name')}</th>
                      <th>{t('pages.user.role.col.code')}</th>
                      <th>{t('pages.user.role.col.count')}</th>
                      <th>{t('pages.user.role.col.scope')}</th>
                      <th className="left">{t('pages.user.role.col.description')}</th>
                      <th>{t('pages.user.role.col.action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRoles.length === 0 && (
                      <tr className="mg-empty-row"><td className="mg-empty" colSpan={6}>{t('common.noData')}</td></tr>
                    )}
                    {paginatedRoles.map((role) => (
                      <tr key={role.id} onClick={() => handleRoleClick(role)}>
                        <td className="left">
                          <div className="mg-name">
                            <span className="mg-name-icon"><span className="material-symbols-outlined">shield</span></span>
                            <span className="mg-name-main">{role.name}</span>
                          </div>
                        </td>
                        <td><span className="mg-code">{role.code}</span></td>
                        <td>{t('pages.user.role.countSuffix', { n: role.permissions?.length || 0 })}</td>
                        <td>
                          {role.permissions && role.permissions.length > 0 ? (
                            <span className="mg-tags">
                              {role.permissions.slice(0, 3).map((perm) => <span key={perm.id} className="mg-tag">{perm.name}</span>)}
                              {role.permissions.length > 3 && <span className="mg-tag more">+{role.permissions.length - 3}</span>}
                            </span>
                          ) : <span className="mg-muted">{DASH}</span>}
                        </td>
                        <td className="left">
                          <span className="mg-ellipsis" title={role.description || ''}>{role.description || <span className="mg-muted">{DASH}</span>}</span>
                        </td>
                        <td>
                          <div className="mg-actions" onClick={(e) => e.stopPropagation()}>
                            <button type="button" className="mg-icon-btn" onClick={() => handleRoleClick(role, true)}>
                              <span className="material-symbols-outlined">edit</span>
                            </button>
                            <button type="button" className="mg-icon-btn danger" onClick={() => handleDeleteClick(role)}>
                              <span className="material-symbols-outlined">delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalElements > 0 && (
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  totalElements={totalElements}
                  size={size}
                  onPageChange={setPage}
                  onSizeChange={handleSizeChange}
                />
              )}
            </>
          )}
        </div>

        {/* 권한 그룹 상세 팝업 */}
        {isModalOpen && selectedRole && (
          <>
            <div className="mg-modal-overlay" onClick={handleCloseModal}></div>
            <div className="mg-modal" role="dialog" aria-modal="true">
              <div className="mg-modal-head">
                <h3 className="mg-modal-title">{isEditMode ? t('pages.user.role.detailModal.titleEdit') : t('pages.user.role.detailModal.titleView')}</h3>
                <button type="button" className="mg-icon-btn" onClick={handleCloseModal}>
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <div className={`mg-modal-body mg-form ${isEditMode ? 'editing' : ''}`}>
                <div className="mg-grid">
                  <MgField label={t('pages.user.role.detailModal.groupNameLabel')}>
                    {isEditMode
                      ? <input type="text" name="name" value={editForm.name} onChange={handleInputChange} className="mg-input" placeholder={t('pages.user.role.detailModal.groupNamePlaceholder')} />
                      : <div className="mg-value">{selectedRole.name}</div>}
                  </MgField>
                  <MgField label={t('pages.user.role.detailModal.codeLabel')}>
                    {isEditMode
                      ? <input type="text" name="code" value={editForm.code} onChange={handleInputChange} className="mg-input" placeholder={t('pages.user.role.detailModal.codePlaceholder')} />
                      : <div className="mg-value">{selectedRole.code}</div>}
                  </MgField>
                  <MgField label={t('pages.user.role.detailModal.descriptionLabel')} full>
                    {isEditMode
                      ? <textarea name="description" value={editForm.description} onChange={handleInputChange} className="mg-textarea" placeholder={t('pages.user.role.detailModal.descriptionPlaceholder')} rows="3" />
                      : <div className="mg-value">{selectedRole.description || DASH}</div>}
                  </MgField>
                </div>

                <div className="mg-group">
                  <span className="mg-label">
                    {t('pages.user.role.detailModal.permissionsLabel')}
                    <span className="mg-panel-note">
                      {isEditMode
                        ? t('pages.user.role.permissionSelected', { n: editForm.permissionIds.length })
                        : t('pages.user.role.permissionCount', { n: selectedRole.permissions?.length || 0 })}
                    </span>
                  </span>
                  {isEditMode ? (
                    // 수정 모드: 전체 권한을 카테고리별로 보여 주고 체크박스로 고른다
                    <div className="mg-perm-tree">
                      {Object.entries(groupByCategory(allPermissions, etcLabel)).map(([category, perms]) => (
                        <div key={category} className="mg-perm-group">
                          <div className="mg-perm-head">
                            <span className="material-symbols-outlined">folder</span>
                            {category}
                            <span className="mg-perm-count">({perms.length})</span>
                          </div>
                          <div className="mg-perm-items">
                            {perms.map((perm) => {
                              const checked = editForm.permissionIds.includes(perm.id);
                              return (
                                <label key={perm.id} className={`mg-check ${checked ? 'checked' : ''}`}>
                                  <input type="checkbox" checked={checked} onChange={() => handlePermissionToggle(perm.id)} />
                                  <span className="mg-check-box"><span className="material-symbols-outlined">check</span></span>
                                  {perm.name}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <PermissionTree permissions={selectedRole.permissions} etcLabel={etcLabel} emptyLabel={t('pages.user.role.misc.noPermissions')} />
                  )}
                </div>
              </div>
              <div className="mg-modal-actions">
                {isEditMode ? (
                  <>
                    <button type="button" className="mg-btn secondary" onClick={handleCancelEdit}>{t('pages.user.role.detailModal.cancel')}</button>
                    <button type="button" className="mg-btn primary" onClick={handleUpdateSubmit}>{t('pages.user.role.detailModal.save')}</button>
                  </>
                ) : (
                  <>
                    <button type="button" className="mg-btn secondary" onClick={handleCloseModal}>{t('pages.user.role.detailModal.close')}</button>
                    <button type="button" className="mg-btn primary" onClick={handleEditClick}>{t('pages.user.role.detailModal.edit')}</button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('pages.user.role.deleteModal.title')}
        message={roleToDelete ? t('pages.user.role.deleteModal.message', { name: roleToDelete.name }) : ''}
        confirmText={t('pages.user.role.deleteModal.confirm')}
        cancelText={t('pages.user.role.deleteModal.cancel')}
        type="danger"
      />
    </MainLayout>
  );
}

export default RoleManagementPage;
